import React, { FC } from 'react';

import { ReactComponent as Card } from '@tecton/ComponentRedesign/lib/Illustrations/Card.svg';
import { ReactComponent as CardDark } from '@tecton/ComponentRedesign/lib/Illustrations/CardDark.svg';
import { ReactComponent as CardError } from '@tecton/ComponentRedesign/lib/Illustrations/CardError.svg';
import { ReactComponent as Dataflow } from '@tecton/ComponentRedesign/lib/Illustrations/Dataflow.svg';
import { ReactComponent as DataflowDark } from '@tecton/ComponentRedesign/lib/Illustrations/DataflowDark.svg';
import { ReactComponent as DataflowError } from '@tecton/ComponentRedesign/lib/Illustrations/DataflowError.svg';
import { ReactComponent as EmptySearch } from '@tecton/ComponentRedesign/lib/Illustrations/EmptySearch.svg';
import { ReactComponent as EmptySearchDark } from '@tecton/ComponentRedesign/lib/Illustrations/EmptySearchDark.svg';
import { ReactComponent as EmptyTable } from '@tecton/ComponentRedesign/lib/Illustrations/EmptyTable.svg';
import { ReactComponent as EmptyTableDark } from '@tecton/ComponentRedesign/lib/Illustrations/EmptyTableDark.svg';
import { ReactComponent as EmptyTableError } from '@tecton/ComponentRedesign/lib/Illustrations/EmptyTableError.svg';
import { ReactComponent as Issue } from '@tecton/ComponentRedesign/lib/Illustrations/Issue.svg';
import { ReactComponent as IssueDark } from '@tecton/ComponentRedesign/lib/Illustrations/IssueDark.svg';
import { ReactComponent as IssueError } from '@tecton/ComponentRedesign/lib/Illustrations/IssueError.svg';
import { ReactComponent as NoResults } from '@tecton/ComponentRedesign/lib/Illustrations/NoResults.svg';
import { ReactComponent as NoResultsDark } from '@tecton/ComponentRedesign/lib/Illustrations/NoResultsDark.svg';
import { ReactComponent as NoResultsError } from '@tecton/ComponentRedesign/lib/Illustrations/NoResultsError.svg';
import { ReactComponent as WalletEmpty } from '@tecton/ComponentRedesign/lib/Illustrations/WalletEmpty.svg';
import { ReactComponent as WalletEmptyDark } from '@tecton/ComponentRedesign/lib/Illustrations/WalletEmptyDark.svg';
import { ReactComponent as WalletEmptyError } from '@tecton/ComponentRedesign/lib/Illustrations/WalletEmptyError.svg';

import { ReactComponent as CardErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/CardErrorDark.svg';
import { ReactComponent as DataflowErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/DataflowErrorDark.svg';
import { ReactComponent as EmptySearchErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/EmptySearchDark.svg';
import { ReactComponent as EmptyTableErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/EmptyTableErrorDark.svg';
import { ReactComponent as IssueErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/IssueErrorDark.svg';
import { ReactComponent as NoResultsErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/NoResultsErrorDark.svg';
import { ReactComponent as WalletEmptyErrorDark } from '@tecton/ComponentRedesign/lib/Illustrations/WalletEmptyErrorDark.svg';

import { useTectonTheme } from '@tecton/ComponentRedesign/Theme/ThemeProvider';

// Names must be unique between icons and illustrations, so appending 'Illustration' here
export type IllustrationType =
  | 'CardIllustration'
  | 'DataflowIllustration'
  | 'EmptySearchIllustration'
  | 'EmptyTableIllustration'
  | 'IssueIllustration'
  | 'NoResultsIllustration'
  | 'WalletEmptyIllustration';

interface IllustrationProps {
  illustration: IllustrationType;
  error?: boolean;
}

const IllustrationTypeMap: Record<'light' | 'dark', Record<IllustrationType, React.ReactNode>> = {
  light: {
    CardIllustration: <Card />,
    DataflowIllustration: <Dataflow />,
    EmptySearchIllustration: <EmptySearch />,
    EmptyTableIllustration: <EmptyTable />,
    IssueIllustration: <Issue />,
    NoResultsIllustration: <NoResults />,
    WalletEmptyIllustration: <WalletEmpty />,
  },
  dark: {
    CardIllustration: <CardDark />,
    DataflowIllustration: <DataflowDark />,
    EmptySearchIllustration: <EmptySearchDark />,
    EmptyTableIllustration: <EmptyTableDark />,
    IssueIllustration: <IssueDark />,
    NoResultsIllustration: <NoResultsDark />,
    WalletEmptyIllustration: <WalletEmptyDark />,
  },
};

const IllustrationTypeMapError: Record<'light' | 'dark', Record<IllustrationType, React.ReactNode>> = {
  light: {
    CardIllustration: <CardError />,
    DataflowIllustration: <DataflowError />,
    EmptySearchIllustration: <EmptySearch />,
    EmptyTableIllustration: <EmptyTableError />,
    IssueIllustration: <IssueError />,
    NoResultsIllustration: <NoResultsError />,
    WalletEmptyIllustration: <WalletEmptyError />,
  },
  dark: {
    CardIllustration: <CardErrorDark />,
    DataflowIllustration: <DataflowErrorDark />,
    EmptySearchIllustration: <EmptySearchErrorDark />,
    EmptyTableIllustration: <EmptyTableErrorDark />,
    IssueIllustration: <IssueErrorDark />,
    NoResultsIllustration: <NoResultsErrorDark />,
    WalletEmptyIllustration: <WalletEmptyErrorDark />,
  },
};

const Illustration: FC<IllustrationProps> = ({ illustration, error = false }) => {
  const { theme } = useTectonTheme();

  if (error) {
    return (
      <div style={{ display: 'contents' }} data-testid="illustration">
        {IllustrationTypeMapError[theme.v1.mode][illustration]}
      </div>
    );
  }

  return (
    <div style={{ display: 'contents' }} data-testid="illustration">
      {IllustrationTypeMap[theme.v1.mode][illustration]}
    </div>
  );
};

export default Illustration;
